import type { UserRole } from '../lib/types';
import type { IconName } from './Icon';

/**
 * What the rail shows, for a role and the centre they have open.
 *
 * A platform administrator belongs to no centre, so the teaching screens —
 * library, students, results — need one chosen before they can show anything.
 * They are listed all the same, locked, because a rail that simply drops them
 * reads as data that has gone missing. That is not a hypothetical: it has been
 * reported as lost work three times, and each time the papers and students
 * were exactly where they were left.
 */

export interface NavItem {
  to: string;
  label: string;
  /** Drawn beside the label, so the rail can be scanned by shape as well as read. */
  icon: IconName;
}

export interface NavGroup {
  section: string;
  items: NavItem[];
  /** Shown, but not reachable until a centre is open. */
  locked?: boolean;
  /** Why it is locked, in the rail. */
  hint?: string;
}

const PLATFORM: NavGroup = {
  section: 'Platform',
  items: [
    { to: '/admin/organizations', label: 'Organisations', icon: 'building' },
    { to: '/admin/instructions', label: 'Instructions', icon: 'film' },
  ],
};

const TEACHING: NavItem[] = [
  // Where the day starts: what is waiting, what is coming, what came in.
  { to: '/today', label: 'Today', icon: 'sun' },
  { to: '/tests', label: 'Test library', icon: 'book' },
  { to: '/students', label: 'Students', icon: 'users' },
  { to: '/schedule', label: 'Test schedule', icon: 'calendar' },
  { to: '/results', label: 'Results', icon: 'award' },
  { to: '/analytics', label: 'Analytics', icon: 'chart' },
];

/*
 * What this platform is and is not — no affiliation with any examination
 * board, no qualification awarded, whose trade marks are whose. In the rail
 * rather than a footer because a centre owner selling practice to students
 * needs to be able to find it and point at it.
 */
const LEGAL: NavItem = { to: '/legal', label: 'About & legal', icon: 'scale' };

const WORKSPACE: NavGroup = {
  section: 'Workspace',
  items: [
    { to: '/activity', label: 'Activity', icon: 'pulse' },
    { to: '/settings/branding', label: 'Appearance', icon: 'palette' },
    LEGAL,
  ],
};

/**
 * A teacher's workspace is About & legal alone. The activity log names every
 * colleague's students and every password reset in the centre, and the
 * appearance is one setting for everybody who teaches there — both are the
 * administrator's, and the server refuses a teacher either way.
 */
const TEACHER_WORKSPACE: NavGroup = { section: 'Workspace', items: [LEGAL] };

/**
 * Managing teachers belongs to the administrator alone — a teacher able to
 * edit colleagues would mean one compromised account could take over the whole
 * centre — so it is added here rather than in the instructor's own rail.
 */
const ADMIN_TEACHING: NavItem[] = [...TEACHING, { to: '/teachers', label: 'Teachers', icon: 'cap' }];

export function navFor(role: UserRole, centreOpen: boolean): NavGroup[] {
  if (role === 'INSTRUCTOR') {
    return [{ section: 'Teaching', items: TEACHING }, TEACHER_WORKSPACE];
  }
  if (role !== 'SUPER_ADMIN') return [];

  if (!centreOpen) {
    return [
      PLATFORM,
      {
        section: 'Teaching',
        items: ADMIN_TEACHING,
        locked: true,
        hint: 'Open a centre to work in it',
      },
    ];
  }
  return [PLATFORM, { section: 'Teaching', items: ADMIN_TEACHING }, WORKSPACE];
}

/**
 * The centre to open on sign-in when there is no room for doubt.
 *
 * One centre and no choice made is not a choice worth asking for — and asking
 * is what made the teaching screens look deleted.
 */
export const soleCentre = <T extends { id: string; name: string }>(
  organizations: T[],
): { id: string; name: string } | null =>
  organizations.length === 1 ? { id: organizations[0]!.id, name: organizations[0]!.name } : null;

/**
 * Whether to go looking for a centre to open by itself.
 *
 * Only for an administrator, only once the session is known, only when none is
 * open — and never after they have closed one on purpose. That last clause is
 * the whole point: without it, leaving a centre on a deployment that has one
 * centre re-opened it on the next render, and the button that says "Leave this
 * centre" did nothing at all.
 */
export function shouldOpenSoleCentre(state: {
  ready: boolean;
  role: UserRole | undefined;
  centreOpen: boolean;
  leftDeliberately: boolean;
}): boolean {
  if (!state.ready) return false;
  if (state.role !== 'SUPER_ADMIN') return false;
  if (state.centreOpen) return false;
  return !state.leftDeliberately;
}

/**
 * Where to land after switching centres.
 *
 * The same screen, where that screen is a list: an administrator comparing two
 * centres' results wants the other centre's results, not to be dropped back at
 * the library every time. Anything holding an id in the URL — one paper, one
 * sitting — belongs to the centre being left, and would be a 404 in the one
 * being opened, so those go to the library instead.
 */
const SCOPED_LISTS = [
  '/today',
  '/tests',
  '/students',
  '/schedule',
  '/results',
  '/analytics',
  '/teachers',
  '/settings/branding',
];

export function switchDestination(pathname: string): string {
  const match = SCOPED_LISTS.find(
    (list) => pathname === list || pathname === `${list}/`,
  );
  return match ?? '/tests';
}
